import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Music2, Video, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const VARIANTS = {
  tiktok: {
    label: "As seen on TikTok",
    Icon: Music2,
    accent: "#EC4899",
    accent_bg: "bg-pink-50",
    accent_text: "text-pink-700",
    accent_border: "border-pink-200",
    headline: <>Charged after you cancelled? <span className="brand-gradient-text">Get it back in under a week.</span></>,
    subhead: "The exact 4-message script TikTok is talking about. Copy-paste, send, get refunded.",
    cta: "Try the viral refund script",
    proof: [
      { name: "Maya R.", said: "Got my $47 back from Peacock in 3 days 🙏" },
      { name: "Jordan T.", said: "Message 3 is a menace — instant refund." },
      { name: "Sam K.", said: "Better than yelling at customer support 😭" },
    ],
  },
  facebook: {
    label: "Recommended on Facebook",
    Icon: Video,
    accent: "#1877F2",
    accent_bg: "bg-blue-50",
    accent_text: "text-blue-700",
    accent_border: "border-blue-200",
    headline: <>Won't take "no" from your subscription. <span className="brand-gradient-text">We give you the words.</span></>,
    subhead: "A calm, step-by-step way to recover charges after you cancelled. Written by people, filled by AI, sent by you.",
    cta: "Recover my charge — $19",
    proof: [
      { name: "Linda H.", said: "Cancelled the gym in January. They charged me twice. Got both back." },
      { name: "Robert M.", said: "Adobe fought me — Message 4 ended it in a day." },
      { name: "Karen W.", said: "Wish I had this two years ago." },
    ],
  },
};

export default function AdLanding() {
  const [params] = useSearchParams();
  const [variantKey, setVariantKey] = useState("tiktok");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // pick variant from path (we're at /l/:variant via router) — but easier: from URL segment
    const seg = window.location.pathname.split("/").pop();
    if (VARIANTS[seg]) setVariantKey(seg);
    api.get("/stats/public").then((r) => setStats(r.data)).catch(()=>{});
  }, []);

  const V = VARIANTS[variantKey];
  const Icon = V.Icon;
  const src = params.get("utm_source") || variantKey;

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center" data-testid={`ad-landing-${variantKey}`}>
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${V.accent_bg} ${V.accent_text} ${V.accent_border} border text-xs font-semibold mb-6`}>
          <Icon className="w-3.5 h-3.5" /> {V.label}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] max-w-3xl mx-auto" data-testid="ad-headline">
          {V.headline}
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">{V.subhead}</p>

        <div className="mt-8">
          <Link to={`/auth?utm=${encodeURIComponent(src)}`} data-testid="ad-primary-cta">
            <Button size="lg" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-8 h-14 text-base font-semibold">
              {V.cta} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {stats && stats.resolved_case_count > 0 && (
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-600 flex-wrap" data-testid="ad-social-proof">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> ${Math.round(stats.total_recovered).toLocaleString()} recovered</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" /> {stats.resolved_case_count} wins</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {stats.total_users} people helped</div>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-4 mt-12 text-left">
          {V.proof.map((q, i) => (
            <Card key={i} className="trust-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-700">"{q.said}"</p>
                <p className="text-xs text-slate-500 mt-3">— {q.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-xs text-slate-500 mt-10 max-w-xl mx-auto">
          $19 per case, non-refundable. We draft and guide — refunds are not guaranteed. Not a law firm.
        </p>
      </section>
    </div>
  );
}
